import { Box, CircularProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AdminLayout from './components/Layout/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import { useI18n } from './contexts/I18nContext';
import { FeatureFlagsProvider } from '@shared/hooks/useFeatureFlag';
import FeatureRoute from '@shared/components/FeatureRoute/FeatureRoute';
import ErrorBoundary from '@shared/components/ErrorBoundary/ErrorBoundary';

// Lazy-loaded pages for code splitting
const Login = lazy(() => import('./pages/Auth/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const UserManagement = lazy(() => import('./pages/Users/UserManagement'));
const ProductManagement = lazy(
  () => import('./pages/Products/ProductManagement')
);
const ProductVariants = lazy(() => import('./pages/Products/ProductVariants'));
const ProductBundles = lazy(() => import('./pages/Products/ProductBundles'));
const ProductBadges = lazy(() => import('./pages/Products/ProductBadges'));
const CategoryManagement = lazy(
  () => import('./pages/Categories/CategoryManagement')
);
const OrderManagement = lazy(() => import('./pages/Orders/OrderManagement'));
const OrderDetail = lazy(() => import('./pages/Orders/OrderDetail'));
const BatchList = lazy(() => import('./pages/Batches/BatchList'));
const BatchDetail = lazy(() => import('./pages/Batches/BatchDetail'));
const BatchCreate = lazy(() => import('./pages/Batches/BatchCreate'));
const Analytics = lazy(() => import('./pages/Analytics/Analytics'));
const AdvancedAnalytics = lazy(
  () => import('./pages/Analytics/AdvancedAnalytics')
);
const Settings = lazy(() => import('./pages/Settings/Settings'));
const CustomizationSettings = lazy(
  () => import('./pages/Settings/CustomizationSettings')
);
const Inventory = lazy(() => import('./pages/Inventory/Inventory'));
const InventoryList = lazy(() => import('./pages/Inventory/InventoryList'));
const InventoryCreate = lazy(() => import('./pages/Inventory/InventoryCreate'));
const InventoryDetail = lazy(() => import('./pages/Inventory/InventoryDetail'));
const InventoryReport = lazy(() => import('./pages/Inventory/InventoryReport'));
const SerialList = lazy(() => import('./pages/Serials/SerialList'));

// Purchases
const PurchaseList = lazy(() => import('./pages/Purchases/PurchaseList'));
const PurchaseDetail = lazy(() => import('./pages/Purchases/PurchaseDetail'));
const PurchaseCreate = lazy(() => import('./pages/Purchases/PurchaseCreate'));

// Integrations
const IntegrationsPage = lazy(
  () => import('./pages/Integrations/IntegrationsPage')
);
const IntegrationProductsPage = lazy(
  () => import('./pages/Integrations/IntegrationProductsPage')
);
const MoySkladIntegration = lazy(
  () => import('./pages/Integrations/MoySkladIntegration')
);
const WebhooksPage = lazy(() => import('./pages/Integrations/WebhooksPage'));
const OneCInstallerDownload = lazy(
  () => import('./pages/Integrations/OneCInstallerDownload')
);
const ConnectorSettingsPage = lazy(
  () => import('./pages/Integrations/ConnectorSettingsPage')
);

// Suppliers
const SupplierList = lazy(() => import('./pages/Suppliers/SupplierList'));
const SupplierCreate = lazy(() => import('./pages/Suppliers/SupplierCreate'));
const SupplierDetail = lazy(() => import('./pages/Suppliers/SupplierDetail'));

// Materials
const MaterialsDatabase = lazy(
  () => import('./pages/Materials/MaterialsDatabase')
);

// Manufacturing / BOM
const BOMList = lazy(() => import('./pages/Manufacturing/BOMList'));
const BOMCreate = lazy(() => import('./pages/Manufacturing/BOMCreate'));
const BOMDetail = lazy(() => import('./pages/Manufacturing/BOMDetail'));
const ManufacturingDashboard = lazy(
  () => import('./pages/Manufacturing/ManufacturingDashboard')
);
const ManufacturingOrders = lazy(
  () => import('./pages/Manufacturing/ManufacturingOrders')
);

// Production Tracker
const ProductionBoard = lazy(() => import('./pages/Production/ProductionBoard'));
const ProductionDetail = lazy(() => import('./pages/Production/ProductionDetail'));
const ProductionMyTasks = lazy(() => import('./pages/Production/ProductionMyTasks'));
const SpecificationEditor = lazy(() => import('./pages/Production/SpecificationEditor'));
const PurchaserView = lazy(() => import('./pages/Production/PurchaserView'));
const AccountingView = lazy(() => import('./pages/Production/AccountingView'));

// RBAC
const RolesManagement = lazy(() => import('./pages/Users/RolesManagement'));

// Organizations (Super Admin only)
const OrganizationList = lazy(
  () => import('./pages/Organizations/OrganizationList')
);
const OrganizationCreate = lazy(
  () => import('./pages/Organizations/OrganizationCreate')
);
const OrganizationDetail = lazy(
  () => import('./pages/Organizations/OrganizationDetail')
);

// Super Admin Dashboard & Feature Flags
const SuperAdminDashboard = lazy(
  () => import('./pages/SuperAdmin/SuperAdminDashboard')
);
const FeatureFlagsManager = lazy(
  () => import('./pages/SuperAdmin/FeatureFlagsManager')
);
const OneCDatabasesList = lazy(
  () => import('./pages/SuperAdmin/OneCDatabasesList')
);
const OneCMetrics = lazy(
  () => import('./pages/SuperAdmin/OneCMetrics')
);
const OneCHealthDashboard = lazy(
  () => import('./pages/SuperAdmin/OneCHealthDashboard')
);
const OneCPlatformSyncLogs = lazy(
  () => import('./pages/SuperAdmin/OneCPlatformSyncLogs')
);

// Quality Control (QC)
const QCDashboard = lazy(() => import('./pages/QualityControl/QCDashboard'));
const QCTemplates = lazy(() => import('./pages/QualityControl/QCTemplates'));
const QCInspections = lazy(
  () => import('./pages/QualityControl/QCInspections')
);
const QCHolds = lazy(() => import('./pages/QualityControl/QCHolds'));

// CRM
const CRMDashboard = lazy(() => import('./pages/CRM/CRMDashboard'));
const CRMTasks = lazy(() => import('./pages/CRM/CRMTasks'));
const CRMSegments = lazy(() => import('./pages/CRM/CRMSegments'));
const CRMLoyalty = lazy(() => import('./pages/CRM/CRMLoyalty'));

// Finance
const FinanceDashboard = lazy(() => import('./pages/Finance/FinanceDashboard'));
const FinancePayments = lazy(() => import('./pages/Finance/FinancePayments'));
const FinanceAccounts = lazy(() => import('./pages/Finance/FinanceAccounts'));

// Notifications
const NotificationList = lazy(
  () => import('./pages/Notifications/NotificationList')
);

// Internal Chat
const ChatPage = lazy(() => import('./pages/Chat/ChatPage'));

// Customer Support (customer widget chats)
const CustomerSupportPage = lazy(
  () => import('./pages/CustomerSupport/CustomerSupportPage')
);

function App() {
  const { user, isAuthenticated, loading } = useAuth();
  const { t } = useI18n();

  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      }
    >
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Everything else in admin must be gated */}
        <Route
          path="/*"
          element={
            <ProtectedRoute requireAuth requireAdmin>
              <FeatureFlagsProvider>
              <AdminLayout>
                <ErrorBoundary
                  source="admin"
                  errorTitle={t('errors.pageErrorTitle')}
                  errorMessage={t('errors.pageErrorMessage')}
                  retryText={t('errors.retry')}
                  homeText={t('errors.goHome')}
                >
                <Suspense
                  fallback={
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="60vh"
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    {/* Core — always available */}
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/roles" element={<FeatureRoute flag="rbac"><RolesManagement /></FeatureRoute>} />
                    <Route path="/products" element={<ProductManagement />} />
                    <Route path="/products/:productId/variants" element={<FeatureRoute flag="product_variants"><ProductVariants /></FeatureRoute>} />
                    <Route path="/products/bundles" element={<FeatureRoute flag="product_bundles"><ProductBundles /></FeatureRoute>} />
                    <Route path="/products/badges" element={<FeatureRoute flag="product_badges"><ProductBadges /></FeatureRoute>} />
                    <Route path="/categories" element={<CategoryManagement />} />

                    {/* Inventory — core_warehouse always on */}
                    <Route path="/inventory" element={<Inventory />} />
                    <Route path="/inventory-counting" element={<FeatureRoute flag="inventory_counting"><InventoryList /></FeatureRoute>} />
                    <Route path="/inventory-counting/create" element={<FeatureRoute flag="inventory_counting"><InventoryCreate /></FeatureRoute>} />
                    <Route path="/inventory-counting/reports" element={<FeatureRoute flag="inventory_counting"><InventoryReport /></FeatureRoute>} />
                    <Route path="/inventory-counting/:id" element={<FeatureRoute flag="inventory_counting"><InventoryDetail /></FeatureRoute>} />
                    <Route path="/batches" element={<FeatureRoute flag="batch_tracking"><BatchList /></FeatureRoute>} />
                    <Route path="/batches/create" element={<FeatureRoute flag="batch_tracking"><BatchCreate /></FeatureRoute>} />
                    <Route path="/batches/:batchId" element={<FeatureRoute flag="batch_tracking"><BatchDetail /></FeatureRoute>} />
                    <Route path="/batches/:batchId/edit" element={<FeatureRoute flag="batch_tracking"><BatchDetail /></FeatureRoute>} />
                    <Route path="/serials" element={<FeatureRoute flag="serial_numbers"><SerialList /></FeatureRoute>} />

                    {/* Orders — core always on */}
                    <Route path="/orders" element={<OrderManagement />} />
                    <Route path="/orders/:orderId" element={<OrderDetail />} />

                    {/* Purchases & Suppliers */}
                    <Route path="/purchases" element={<FeatureRoute flag="purchases_module"><PurchaseList /></FeatureRoute>} />
                    <Route path="/purchases/create" element={<FeatureRoute flag="purchases_module"><PurchaseCreate /></FeatureRoute>} />
                    <Route path="/purchases/:id" element={<FeatureRoute flag="purchases_module"><PurchaseDetail /></FeatureRoute>} />

                    {/* Integrations */}
                    <Route path="/integrations" element={<FeatureRoute flag="integrations_module"><IntegrationsPage /></FeatureRoute>} />
                    <Route path="/integrations/:namespace/products" element={<FeatureRoute flag="integrations_module"><IntegrationProductsPage /></FeatureRoute>} />
                    <Route path="/integrations/moysklad" element={<FeatureRoute flag="moysklad_integration"><MoySkladIntegration /></FeatureRoute>} />
                    <Route path="/integrations/webhooks" element={<FeatureRoute flag="webhooks"><WebhooksPage /></FeatureRoute>} />
                    <Route path="/integrations/1c-installer" element={<FeatureRoute flag="onec_module"><OneCInstallerDownload /></FeatureRoute>} />
                    <Route path="/integrations/connector-settings" element={<FeatureRoute flag="onec_module"><ConnectorSettingsPage /></FeatureRoute>} />

                    {/* Suppliers */}
                    <Route path="/suppliers" element={<FeatureRoute flag="suppliers_module"><SupplierList /></FeatureRoute>} />
                    <Route path="/suppliers/create" element={<FeatureRoute flag="suppliers_module"><SupplierCreate /></FeatureRoute>} />
                    <Route path="/suppliers/:id" element={<FeatureRoute flag="suppliers_module"><SupplierDetail /></FeatureRoute>} />

                    {/* Materials */}
                    <Route path="/materials" element={<FeatureRoute flag="materials_module"><MaterialsDatabase /></FeatureRoute>} />

                    {/* Manufacturing / BOM — when BOM enabled, all modules integrate */}
                    <Route path="/manufacturing" element={<FeatureRoute flag="manufacturing_module"><ManufacturingDashboard /></FeatureRoute>} />
                    <Route path="/manufacturing/orders" element={<FeatureRoute flag="manufacturing_module"><ManufacturingOrders /></FeatureRoute>} />
                    <Route path="/manufacturing/bom" element={<FeatureRoute flag="bill_of_materials"><BOMList /></FeatureRoute>} />
                    <Route path="/manufacturing/bom/create" element={<FeatureRoute flag="bill_of_materials"><BOMCreate /></FeatureRoute>} />
                    <Route path="/manufacturing/bom/:id" element={<FeatureRoute flag="bill_of_materials"><BOMDetail /></FeatureRoute>} />
                    <Route path="/manufacturing/bom/:id/edit" element={<FeatureRoute flag="bill_of_materials"><BOMCreate /></FeatureRoute>} />

                    {/* Production Tracker */}
                    <Route path="/production" element={<FeatureRoute flag="production_tracker"><ProductionBoard /></FeatureRoute>} />
                    <Route path="/production/my-tasks" element={<FeatureRoute flag="production_tracker"><ProductionMyTasks /></FeatureRoute>} />
                    <Route path="/production/:id" element={<FeatureRoute flag="production_tracker"><ProductionDetail /></FeatureRoute>} />
                    <Route path="/production/:id/specification" element={<FeatureRoute flag="production_tracker"><SpecificationEditor /></FeatureRoute>} />
                    <Route path="/production/:id/purchasing" element={<FeatureRoute flag="production_tracker"><PurchaserView /></FeatureRoute>} />
                    <Route path="/production/:id/accounting" element={<FeatureRoute flag="production_tracker"><AccountingView /></FeatureRoute>} />

                    {/* Quality Control */}
                    <Route path="/qc" element={<FeatureRoute flag="quality_control"><QCDashboard /></FeatureRoute>} />
                    <Route path="/qc/templates" element={<FeatureRoute flag="quality_control"><QCTemplates /></FeatureRoute>} />
                    <Route path="/qc/inspections" element={<FeatureRoute flag="quality_control"><QCInspections /></FeatureRoute>} />
                    <Route path="/qc/inspections/create" element={<FeatureRoute flag="quality_control"><QCInspections /></FeatureRoute>} />
                    <Route path="/qc/holds" element={<FeatureRoute flag="quality_control"><QCHolds /></FeatureRoute>} />

                    {/* CRM */}
                    <Route path="/crm" element={<FeatureRoute flag="crm_module"><CRMDashboard /></FeatureRoute>} />
                    <Route path="/crm/tasks" element={<FeatureRoute flag="crm_module"><CRMTasks /></FeatureRoute>} />
                    <Route path="/crm/segments" element={<FeatureRoute flag="crm_segments"><CRMSegments /></FeatureRoute>} />
                    <Route path="/crm/loyalty" element={<FeatureRoute flag="crm_loyalty"><CRMLoyalty /></FeatureRoute>} />

                    {/* Finance */}
                    <Route path="/finance" element={<FeatureRoute flag="finance_module"><FinanceDashboard /></FeatureRoute>} />
                    <Route path="/finance/payments" element={<FeatureRoute flag="finance_payments"><FinancePayments /></FeatureRoute>} />
                    <Route path="/finance/accounts" element={<FeatureRoute flag="finance_accounts"><FinanceAccounts /></FeatureRoute>} />

                    {/* Communication */}
                    <Route path="/chat" element={<FeatureRoute flag="messenger_module"><ChatPage /></FeatureRoute>} />
                    <Route path="/customer-support" element={<FeatureRoute flag="customer_support"><CustomerSupportPage /></FeatureRoute>} />

                    {/* Organizations (Super Admin only) */}
                    <Route path="/organizations" element={<OrganizationList />} />
                    <Route path="/organizations/create" element={<OrganizationCreate />} />
                    <Route path="/organizations/:id" element={<OrganizationDetail />} />
                    <Route path="/organizations/:id/edit" element={<OrganizationDetail />} />

                    {/* Super Admin */}
                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                    <Route path="/super-admin/feature-flags" element={<FeatureFlagsManager />} />
                    <Route path="/super-admin/1c-databases" element={<OneCDatabasesList />} />
                    <Route path="/super-admin/1c-metrics" element={<OneCMetrics />} />
                    <Route path="/super-admin/1c-health" element={<OneCHealthDashboard />} />
                    <Route path="/super-admin/1c-sync-logs" element={<OneCPlatformSyncLogs />} />

                    {/* Analytics */}
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/analytics/advanced" element={<FeatureRoute flag="advanced_analytics"><AdvancedAnalytics /></FeatureRoute>} />

                    {/* Notifications */}
                    <Route path="/notifications" element={<NotificationList />} />

                    {/* Settings — always accessible */}
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/customization" element={<FeatureRoute flag="customization"><CustomizationSettings /></FeatureRoute>} />

                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
                </ErrorBoundary>
              </AdminLayout>
              </FeatureFlagsProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
